import React, { useMemo, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useOrganizer } from '../../contexts/OrganizerContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../stores/subscriptionStore';
import { useIsDesktop } from '../../hooks/useMediaQuery';
import { AnalyticsService } from '../../services/AnalyticsService';
import {
  ProfileHeroCard,
  SubscriptionCard,
  ImpactStatsCard,
  ReferralCard
} from './components';
import './OrganizerProfilePrivate.css';

interface OrganizerProfilePrivatePageProps {
  /**
   * Desktop callback: opens ProfileEditModal
   * Mobile: not used (navigates to edit page instead)
   */
  onEditClick?: () => void;
  /**
   * Desktop callback: opens SubscriptionModal
   * Mobile: not used (navigates to subscription page instead)
   */
  onManageSubscriptionClick?: () => void;
}

/**
 * Private profile page for organizers (Dashboard Cards layout)
 * Displays: Hero card (full-width) + Subscription card + Impact card
 *
 * Desktop: Callbacks open modals
 * Mobile: Navigation to separate pages
 */
const OrganizerProfilePrivatePage: React.FC<OrganizerProfilePrivatePageProps> = ({
  onEditClick,
  onManageSubscriptionClick
}) => {
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const {
    organizerProfile,
    loading: profileLoading,
    alltimeKpis,
    analyticsLoading,
    eventCounts
  } = useOrganizer();
  const { subscription, loading: subscriptionLoading, loadSubscription } = useSubscription();
  const isDesktop = useIsDesktop();

  // Load subscription data from Supabase on mount
  useEffect(() => {
    if (organizerProfile?.id) {
      console.log('🔄 [ProfilePage] Loading subscription for organizer:', organizerProfile.id);
      loadSubscription(organizerProfile.id);
    }
  }, [organizerProfile?.id, loadSubscription]);

  // Calculate profile completion using existing AnalyticsService function
  const completion = useMemo(() => {
    if (!organizerProfile) {
      return { score: 0, elementsCompletes: [], elementsManquants: [] };
    }
    return AnalyticsService.calculateProfileCompletionScore(organizerProfile);
  }, [organizerProfile]);

  // Handle edit action - desktop opens modal, mobile navigates
  const handleEditClick = () => {
    if (isDesktop && onEditClick) {
      onEditClick();
    } else {
      navigate('/organizer/profile/edit');
    }
  };

  // Handle view public profile - opens in new tab
  const handleViewPublicClick = () => {
    if (user?.uid) {
      window.open(`/organizer-profile/${user.uid}`, '_blank');
    }
  };

  // Handle subscription management - desktop opens modal, mobile navigates
  const handleManageSubscriptionClick = () => {
    if (isDesktop && onManageSubscriptionClick) {
      onManageSubscriptionClick();
    } else {
      navigate('/organizer/subscription');
    }
  };

  // Use all-time stats for the impact card
  const totalEventsCreated = eventCounts.active + eventCounts.draft + eventCounts.past;
  const impactStats = useMemo(() => ({
    touristsReached: alltimeKpis?.totalViews ?? 0,
    eventsCreated: totalEventsCreated
  }), [alltimeKpis, totalEventsCreated]);

  return (
    <div className="organizer-profile-private">
      {/* Hero Card - Full Width */}
      <ProfileHeroCard
        profile={organizerProfile}
        completionScore={completion.score}
        completedFields={completion.elementsCompletes}
        missingFields={completion.elementsManquants}
        onEditClick={handleEditClick}
        onViewPublicClick={handleViewPublicClick}
        loading={profileLoading}
      />

      {/* Cards Grid - Desktop: Left (Subscription + Referral) | Right (Impact) | Mobile: Subscription, Referral, Impact */}
      <div className="organizer-profile-private__cards-grid">
        {/* Left column - Subscription + Referral stacked */}
        <div className="organizer-profile-private__left-column">
          <SubscriptionCard
            subscription={subscription}
            onManageClick={handleManageSubscriptionClick}
            loading={subscriptionLoading}
          />
          {organizerProfile?.id && organizerProfile?.organizerContact && (
            <ReferralCard
              organizerId={organizerProfile.id}
              organizerEmail={organizerProfile.organizerContact}
            />
          )}
        </div>

        {/* Right column - Impact */}
        <div className="organizer-profile-private__right-column">
          <ImpactStatsCard
            stats={impactStats}
            loading={analyticsLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default OrganizerProfilePrivatePage;
